import React, { useEffect, useState } from "react";

type Disclosure = {
    DisclosureName: string;
    DisclosureMessage?: string;
    DisclosureLink?: string;
};

const generateUniqueId = () => {
    return `id-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`;
};

type QuoteOutcomeDisclosuresProps = {
    disclosures: Disclosure[];
    handleInput: (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => void;
    upsertQuoteInputs: {
        disclosuresRead: string[];
        disclosuresSelectedAsYes: string[];
        disclosuresSelectedAsNo: string[];
        disclosuresSelectedAsNA: string[];
        lineOfBusiness: string;
        stateCode: string;
        source: string;
        quoteOutcome: string;
    };
    horizontalLayout: boolean;
};
//
const QuoteOutcomeDisclosures: React.FC<QuoteOutcomeDisclosuresProps> = ({
    disclosures,
    handleInput,
    upsertQuoteInputs,
    horizontalLayout,
}) => {
    const [selectedRadioButton, setSelectedRadioButton] = useState<string[]>([]);
    const [selectedValues, setSelectedValues] = useState<{ [key: string]: string }>({});
    const uniqueId = generateUniqueId();
    const [isDisclosureEditable, setIsDisclosureEditable] = useState(false);

    useEffect(() => {
        const resetValues: { [key: string]: string } = {};
        disclosures.forEach((disclosure) => {
            resetValues[disclosure.DisclosureName] = ""; // Reset each disclosure's selection
        });
        setSelectedValues(resetValues);
        
        const hasExistingData = upsertQuoteInputs.disclosuresSelectedAsYes?.length > 0 || 
                               upsertQuoteInputs.disclosuresSelectedAsNo?.length > 0 || 
                               upsertQuoteInputs.disclosuresSelectedAsNA?.length > 0;

        let radioPrefill: string[] = Array(disclosures.length).fill("");
        let yesDisclosures: string[] = [];
        let noDisclosures: string[] = [];
        let naDisclosures: string[] = [];
        let readDisclosures: string[] = [];
        if (hasExistingData && !isDisclosureEditable) {
            // Prefill based on existing data
            radioPrefill = disclosures.map((disclosure, index) => {
                if (upsertQuoteInputs.disclosuresSelectedAsYes?.includes(disclosure.DisclosureName)) {
                    return "yes";
                } else if (upsertQuoteInputs.disclosuresSelectedAsNo?.includes(disclosure.DisclosureName)) {
                    return "no";
                } else if (upsertQuoteInputs.disclosuresSelectedAsNA?.includes(disclosure.DisclosureName)) {
                    return "na";
                }
                return "";
            });
            setIsDisclosureEditable(true);
            yesDisclosures = upsertQuoteInputs.disclosuresSelectedAsYes || [];
            noDisclosures = upsertQuoteInputs.disclosuresSelectedAsNo || [];
            naDisclosures = upsertQuoteInputs.disclosuresSelectedAsNA || [];
            readDisclosures = upsertQuoteInputs.disclosuresRead || [];
        } else if (upsertQuoteInputs.quoteOutcome === "Sale") {
            
            const prefillNames = [
                "Fraud Statement",
                "Credit Disclosure",
                "New Business Closing Statement"
            ];

            radioPrefill = disclosures.map(d =>
                prefillNames.includes(d.DisclosureName) ? "yes" : ""
            );
            yesDisclosures = disclosures
                .filter(d => prefillNames.includes(d.DisclosureName))
                .map(d => d.DisclosureName);
            readDisclosures = yesDisclosures;
        }

        setSelectedRadioButton(radioPrefill);

        handleInput({
            target: {
                name: "disclosuresRead",
                value: readDisclosures,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>);

        handleInput({
            target: {
                name: "disclosuresSelectedAsYes",
                value: yesDisclosures,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>);

        handleInput({
            target: {
                name: "disclosuresSelectedAsNo",
                value: noDisclosures,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>);

        handleInput({
            target: {
                name: "disclosuresSelectedAsNA",
                value: naDisclosures,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
    }, [upsertQuoteInputs.lineOfBusiness,
    upsertQuoteInputs.stateCode,
    upsertQuoteInputs.source,
    upsertQuoteInputs.quoteOutcome]);

    const handleDisclosureChange = (
        index: number,
        selectedButton: string,
        disclosureName: string
    ) => {
        const updatedDisclosuresRead = upsertQuoteInputs.disclosuresRead.includes(disclosureName)
            ? [...upsertQuoteInputs.disclosuresRead]
            : [...upsertQuoteInputs.disclosuresRead, disclosureName];

        handleInput({
            target: {
                name: "disclosuresRead",
                value: updatedDisclosuresRead,
            },
        } as unknown as React.ChangeEvent<HTMLInputElement>);

        if (selectedButton === "yes") {
            const updatedDisclosuresSelectedAsYes = [
                ...(upsertQuoteInputs.disclosuresSelectedAsYes || []),
                disclosureName,
            ];

            handleInput({
                target: {
                    name: "disclosuresSelectedAsYes",
                    value: updatedDisclosuresSelectedAsYes,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);

            const updatedDisclosuresSelectedAsNo = upsertQuoteInputs.disclosuresSelectedAsNo.filter(
                (name) => name !== disclosureName
            );

            const updatedDisclosuresSelectedAsNA = upsertQuoteInputs.disclosuresSelectedAsNA.filter(
                (name) => name !== disclosureName
            );

            handleInput({
                target: {
                    name: "disclosuresSelectedAsNo",
                    value: updatedDisclosuresSelectedAsNo,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);

            handleInput({
                target: {
                    name: "disclosuresSelectedAsNA",
                    value: updatedDisclosuresSelectedAsNA,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
        } else if (selectedButton === "no") {
            const updatedDisclosuresSelectedAsNo = [
                ...(upsertQuoteInputs.disclosuresSelectedAsNo || []),
                disclosureName,
            ];

            handleInput({
                target: {
                    name: "disclosuresSelectedAsNo",
                    value: updatedDisclosuresSelectedAsNo,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);

            const updatedDisclosuresSelectedAsYes = upsertQuoteInputs.disclosuresSelectedAsYes.filter(
                (name) => name !== disclosureName
            );

            const updatedDisclosuresSelectedAsNA = upsertQuoteInputs.disclosuresSelectedAsNA.filter(
                (name) => name !== disclosureName
            );

            handleInput({
                target: {
                    name: "disclosuresSelectedAsYes",
                    value: updatedDisclosuresSelectedAsYes,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);

            handleInput({
                target: {
                    name: "disclosuresSelectedAsNA",
                    value: updatedDisclosuresSelectedAsNA,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
        } else if (selectedButton === "na") {
            const updatedDisclosuresSelectedAsNA = [
                ...(upsertQuoteInputs.disclosuresSelectedAsNA || []),
                disclosureName,
            ];

            handleInput({
                target: {
                    name: "disclosuresSelectedAsNA",
                    value: updatedDisclosuresSelectedAsNA,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);

            const updatedDisclosuresSelectedAsYes = upsertQuoteInputs.disclosuresSelectedAsYes.filter(
                (name) => name !== disclosureName
            );

            const updatedDisclosuresSelectedAsNo = upsertQuoteInputs.disclosuresSelectedAsNo.filter(
                (name) => name !== disclosureName
            );

            handleInput({
                target: {
                    name: "disclosuresSelectedAsYes",
                    value: updatedDisclosuresSelectedAsYes,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);

            handleInput({
                target: {
                    name: "disclosuresSelectedAsNo",
                    value: updatedDisclosuresSelectedAsNo,
                },
            } as unknown as React.ChangeEvent<HTMLInputElement>);
        }
    };

    return (
        <div
            className={horizontalLayout ? "disclosures-container-horizontal" : ""}
        >
            {disclosures.map((disclosure, index) => (
                <form
                    key={index}
                    className={horizontalLayout ? "disclosure-item-horizontal" : ""}
                >
                    <fieldset
                        role="radiogroup"
                        className="form-field radio-button-group"
                        style={!horizontalLayout ? { display: "flex", flexDirection: "column", marginBottom: "16px" } : {}}
                    >
                        <legend>
                            {disclosure.DisclosureName}
                            {disclosure.DisclosureLink && (
                                <a
                                    href={disclosure.DisclosureLink}
                                    target="_blank"
                                    style={{ marginLeft: "8px" }}
                                >
                                    Link to KT
                                </a>
                            )}
                        </legend>
                        {disclosure.DisclosureMessage && (
                            <p style={{ fontStyle: "italic", marginBottom: "4px", fontSize: "0.85em" }}>
                                {disclosure.DisclosureMessage}
                            </p>
                        )}
                        <div style={{ display: "flex", minWidth: "100%" }}>
                            <input
                                id={`radio-yes-${index}-${uniqueId}`}
                                type="radio"
                                name={`redirectAnswer-${index}`}
                                value="yes"
                                checked={selectedRadioButton[index] === "yes"}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedRadioButton((prev) => {
                                        const updated = [...prev];
                                        updated[index] = value;
                                        return updated;
                                    });
                                    handleDisclosureChange(index, value, disclosure.DisclosureName);
                                }}
                            />
                            <label
                                className="radio"
                                htmlFor={`radio-yes-${index}-${uniqueId}`}
                                style={{ width: "33%", margin: "4px" }}
                            >
                                <span>Yes</span>
                            </label>
                            <input
                                id={`radio-no-${index}-${uniqueId}`}
                                type="radio"
                                name={`redirectAnswer-${index}`}
                                value="no"
                                checked={selectedRadioButton[index] === "no"}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedRadioButton((prev) => {
                                        const updated = [...prev];
                                        updated[index] = value;
                                        return updated;
                                    });
                                    handleDisclosureChange(index, value, disclosure.DisclosureName);
                                }}
                            />
                            <label
                                className="radio"
                                htmlFor={`radio-no-${index}-${uniqueId}`}
                                style={{ width: "33%", margin: "4px" }}
                            >
                                <span>No</span>
                            </label>
                            <input
                                id={`radio-na-${index}-${uniqueId}`}
                                type="radio"
                                name={`redirectAnswer-${index}`}
                                value="na"
                                checked={selectedRadioButton[index] === "na"}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedRadioButton((prev) => {
                                        const updated = [...prev];
                                        updated[index] = value;
                                        return updated;
                                    });
                                    handleDisclosureChange(index, value, disclosure.DisclosureName);
                                }}
                            />
                            <label
                                className="radio"
                                htmlFor={`radio-na-${index}-${uniqueId}`}
                                style={{ width: "33%", margin: "4px" }}
                            >
                                <span>NA</span>
                            </label>
                        </div>
                    </fieldset>
                </form>
            ))}
        </div>
    );
};

export default QuoteOutcomeDisclosures;